
/**
 * Imports - External
 * --------------------------------------------------------------------------
 */
import { faArrowsRotate } from '@fortawesome/pro-duotone-svg-icons';
import { faDna, faUser } from '@fortawesome/duotone-light-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Box,
    BoxProps,
    Button,
    Checkbox,
    CircularProgress,
    darken,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    MenuItem,
    MenuItemProps,
    Paper,
    Skeleton,
    styled,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import { green } from '@mui/material/colors';
import { FC, memo, MouseEventHandler, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import { Virtuoso } from 'react-virtuoso';

/**
 * Imports - Internal
 * --------------------------------------------------------------------------
 */
import { DeleteTrialParticipantCacheData, Institution, Participant, ParticipantMetadata } from '@/apis/dtos';
import Center from '@/components/common/Center';
import CustomSelect from '@/components/common/Select';
import TrialCoordinatorSelectDialog from '@/components/dialogs/TrialCoordinatorSelectDialog';
import Loading from '@/components/layout/Loading';
import Show from '@/components/layout/Show';
import { TrialContext } from '@/contexts/data/TrialContext';
import { TrialInstitutionCoordinatorsContext } from '@/contexts/data/TrialInstitutionCoordinatorsContext';
import TrialsMetadataContext from '@/contexts/data/TrialsMetadataContext';
import { useBreadcrumbs } from '@/contexts/layout/BreadcrumbContext';
import useServiceStackClient from '@/hooks/apis/useServiceStackClient';
import useGetParticipants from '@/hooks/data/useGetParticipants';
import useGetTrialCoordinatingGroups from '@/hooks/data/useGetTrialCoordinatingGroups';
import useGetTrialInstitutionCoordinators from '@/hooks/data/useGetTrialInstitutionCoordinators';
import useGetTrialInstitutions from '@/hooks/data/useGetTrialInstitutions';
import useGetTrialMasterGroups from '@/hooks/data/useGetTrialMasterGroups';
import { themes } from '@/styles/DocumentVerificationTheme';

/**
 * Interfaces
 * --------------------------------------------------------------------------
 */

interface IParticipantListItemProps {
    index: number;
    participant: ParticipantMetadata;
    institutions: Record<number, Institution>;
    onClick?: MouseEventHandler<HTMLLIElement>;
}

/**
 * Components
 * --------------------------------------------------------------------------
 */

const VerticalCenter = styled(Box, { shouldForwardProp: (props) => props !== 'toRight' && props !== 'toCenter' && props !== 'toBottom' })<BoxProps & { toRight?: boolean, toCenter?: boolean, toBottom?: boolean }>(({ toRight, toCenter, toBottom }) => ({
    height: '100%',
    display: 'flex',
    justifyContent: toCenter ? 'center' : toRight ? 'right' : 'left',
    alignItems: toBottom ? 'end' : 'center',
}));

const StickySearch = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    position: 'sticky',
    top: 48 /* header height */ + 24,
    
    '@media (min-width: 600px)': {
        top: 64 /* header height */ + 24,
    },
}));

const GridSearchColumn = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        order: 2
    }
}));

const StyledParticipantMenuItem = styled(MenuItem, { shouldForwardProp: (props) => props !== 'ha' && props !== 'va' })<MenuItemProps & { index: number }>(({ index, theme }) => ({
    backgroundColor: theme.palette.mode === 'light' 
        ? (index % 2 === 1 ? '#f5f5f5' : "#fff")
        : (index % 2 === 1 ? '#262626' : "#2e2e2e"),
    transition: '0.15s',
    padding: '0px',
}));

const StyledParticipantDocumentCount = styled(Box, { shouldForwardProp: (props) => props !== "ratio" })<BoxProps & { ratio: number }>(({ ratio }) => ({
    padding: '5px 10px',
    backgroundColor: `${ratio <= 0 ?
            themes.allPendingTotal :
        ratio > 0 && ratio < 100 ?
            themes.somePendingTotal :
            themes.nonePendingBackground}`,
    border: `${ratio <= 0 ? 
            themes.allPendingTotalBorder : 
        ratio > 0 && ratio < 100 ?
            themes.somePendingTotalBorder :
            themes.nonePendingBorder}`,
    transition: '0.2s',
    borderRadius: '10px'
}));

const StyledParticipantRatioBar = styled(Box, { shouldForwardProp: (props) => props !== "ratio" })<BoxProps & { ratio: number }>(({ ratio }) => ({
    top: 0,
    width: `${ratio}%`,
    height: '100%',
    backgroundColor: `${ratio <= 0 ?
            themes.allPendingBackground :
        ratio > 0 && ratio < 100 ?
            themes.somePendingBackground :
            themes.nonePendingBackground}`,
    border: `${ratio <= 0 ? 
            themes.allPendingBorder : 
        ratio > 0 && ratio < 100  ?
            themes.somePendingBorder :
            themes.nonePendingBorder}`,
    transition: '0.2s'
}));

const StyledIconButton = styled(IconButton)(() => (({
    width: '36px',
    height: '36px'
})));

const ParticipantListItem: FC<IParticipantListItemProps> = ({
    index,
    participant,
    institutions,
    onClick
}) => {
    const theme = useTheme();

    const handleRatio = useCallback((verified: number, total: number) => {
        if (total <= 0) return 0;
        if (verified > total) return 100;

        return verified / total * 100;
    }, []);

    const ratio = useMemo(() => {
        var verified = participant.verifiedDocuments ?? 0;
        var total = participant.totalDocuments ?? 0;

        if (verified > total) {
            verified = total;
        }

        return handleRatio(verified, total);
    }, [participant.verifiedDocuments, participant.totalDocuments, handleRatio]);

    const verified = useMemo(() => {
        if ((participant?.verifiedDocuments ?? 0) > (participant?.totalDocuments ?? 0)) {
            return participant?.totalDocuments;
        }
        return participant?.verifiedDocuments;
    }, [participant.verifiedDocuments, participant.totalDocuments]);

    return (
        <StyledParticipantMenuItem
            index={index}
            onClick={onClick}
        >
            <Box sx={{ 
                position: 'absolute',
                top: 0,
                right: 0,
                left: '60px',
                height: '100%'
            }}>
                <StyledParticipantRatioBar ratio={ratio}/>
            </Box>
            <Center height='60px'>
                <Center display='inline-flex' bgcolor={theme.palette.primary.main} height='60px' width='60px'>
                    <FontAwesomeIcon color='white' fontSize='2.3rem' icon={faUser} />
                </Center>
                <Center ha='start' display='inline-flex' height='60px' width='90px' padding='0 8px'>
                    <List>
                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.mode === 'light' ? '#000' : '#fff',
                            }}
                        >
                            {participant.label}
                        </Typography>
                        <Box 
                            sx={{ 
                                display: 'inline', 
                                fontSize: '0.6rem',
                                color: theme.palette.mode === 'light' ? '#000' : '#fff'

                            }}
                        >
                            {!!participant?.institutionId && institutions[participant?.institutionId]?.name}
                        </Box>
                    </List>
                </Center>
            </Center>
            <Center ha='end' width='100%' height='100%' paddingRight='8px'>
                {
                    participant.totalDocuments !== undefined && participant.verifiedDocuments !== undefined ?
                    <StyledParticipantDocumentCount
                        ratio={ratio}
                    >
                        <Typography 
                            variant='body1'
                            color={
                                ratio <= 0 ?
                                    themes.allPendingColor :
                                ratio > 0 && ratio < 100 ?
                                    themes.somePendingColor :
                                    themes.nonePendingColor
                            }
                            fontWeight='500'
                        >
                            {verified} / {participant.totalDocuments}
                        </Typography>
                    </StyledParticipantDocumentCount> :
                    <Skeleton width='80px' height='60px'/>
                }
            </Center>
        </StyledParticipantMenuItem>
    );
}

interface IVirtuosoContext<TData extends Participant = any, TAdditional extends object = any> {
    trialCode: string;
    theme: Theme;
    navigate: NavigateFunction;
    institutionMap: Record<number, Institution>;
}

interface IVirtuosoInnerItem<TData extends Participant = any, TAdditional extends object = any> {
    index: number;
    item: TData;
    context: IVirtuosoContext<TData, TAdditional>;
}


const InnerItem = <TData extends Participant = any, TAdditional extends object = any>({ 
    index,
    item,
    context
}: IVirtuosoInnerItem<TData, TAdditional>) => {
    return (
        <ParticipantListItem 
            index={index}
            key={`participant-${item.id}`}
            participant={item} 
            institutions={context.institutionMap} 
            onClick={() => context.navigate(`/${context.trialCode}/${item.label}`)}
        />
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = <TData extends Participant = any, TAdditional extends object = any>(index: number, item: TData, context: IVirtuosoContext<TData, TAdditional>) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}

const ParticipantsPage: FC = () => {
    //
    // -- Hooks --
    //
    const navigate = useNavigate();
    const theme = useTheme();
    const { trialCode } = useParams();
    const client = useServiceStackClient();

    //
    // -- Contexts --
    //
    const { data: trial, loading: trialLoading, validating: trialValidating, emailSite } = useContext(TrialContext);
    const { data: trialMetadatas, loading: trialMetaLoading } = useContext(TrialsMetadataContext);

    //
    // -- States --
    //
    const [displayVerified, setDisplayVerified] = useSessionStorage<boolean>(`${trialCode}-verified`, false);
    const [displayEmpty, setDisplayEmpty] = useSessionStorage<boolean>(`${trialCode}-empty`, false);
    const [institute, setInstitute] = useSessionStorage<string>(`${trialCode}-institute`, 'All');
    const [masterGroup, setMasterGroup] = useSessionStorage<string>(`${trialCode}-master-group`, 'All');
    const [coordinatingGroup, setCoordinatingGroup] = useSessionStorage<string>(`${trialCode}-coordinating-group`, 'All');
    const [coordinatorSelectDialogOpen, setCoordinatorSelectDialogOpen] = useState(false);

    //
    // -- Queries --
    //
    const { data: instituteData, loading: instituteDataLoading, validating: instituteDataValidating } = useGetTrialInstitutions(trial ? { trialId: trial.id } : undefined, { revalidateOnFocus: false });
    const { data: masterGroupData, loading: masterGroupLoading } = useGetTrialMasterGroups(trial ? { trialId: trial.id } : undefined, { revalidateOnFocus: false });
    const { data: coordinatingGroupData, loading: coordinatingGroupLoading } = useGetTrialCoordinatingGroups(trial ? { trialId: trial.id } : undefined, { revalidateOnFocus: false });

    const instituteCode = useMemo(() => instituteData?.find(institution => institution.name === institute)?.code, [instituteData, institute]);
    const trialCoordinatorsContextData = useGetTrialInstitutionCoordinators(trial && instituteCode ? { trialId: trial.id, institutionCode: instituteCode}: undefined);
    
    //
    // -- Cached --
    //
    const trialVerified = useMemo(() => {
        const trialMetadata = trialMetadatas?.find(x => x.id === trial?.id);
        return !!(trialMetadata && trialMetadata.totalDocuments === trialMetadata.verifiedDocuments);
    }, [trial, trialMetadatas]);

    const filteredMasterGroupsData = useMemo(() => {
        return masterGroupData?.sort((a, b) => {
            if (a.name! < b.name!) return -1;
            if (a.name! > b.name!) return 1;
            return 0;
        });
    }, [masterGroupData]);

    const filteredCoordinatingGroupsData = useMemo(() => {
        let ordered = coordinatingGroupData?.sort((a, b) => {
            if (a.name! < b.name!) return -1;
            if (a.name! > b.name!) return 1;
            return 0;
        });

        if (ordered && masterGroup && masterGroup !== 'All') {
            ordered = ordered?.filter(x => x.masterGroupId === masterGroupData?.find(x => x.name === masterGroup)?.id)
        }

        return ordered;
    }, [coordinatingGroupData, masterGroup, masterGroupData]);

    const institutionMap = useMemo(() => {
        return instituteData?.reduce<Record<number, Institution>>((a, b) => {
            if (!b.id) {
                return a;
            }

            a[b.id] = b;

            return a;
        }, {}) ?? {};
    }, [instituteData]);

    const filteredInstituteData = useMemo(() => {
        let ordered = instituteData?.sort((a, b) => {
            if (a.name! < b.name!) return -1;
            if (a.name! > b.name!) return 1;
            return 0;
        });

        if (ordered && masterGroup && masterGroup !== 'All') {
            ordered = ordered?.filter(x => x.masterGroupId === masterGroupData?.find(x => x.name === masterGroup)?.id)
        }

        if (ordered && coordinatingGroup && coordinatingGroup !== 'All') {
            ordered = ordered?.filter(x => x.coordinatingGroupId === coordinatingGroupData?.find(x => x.name === coordinatingGroup)?.id)
        }

        return ordered;
    }, [instituteData, masterGroup, masterGroupData, coordinatingGroup, coordinatingGroupData]);

    const { data: participantData, loading: participantLoading, validating: participantValidating, refresh } = useGetParticipants(
        trial ? { trialId: trial.id } : undefined, 
        { revalidateOnFocus: false }
    );

    /**
     * Filter the participant data and map the 
     * participant metadata once it is retrieved
     */
    const filteredParticipantData = useMemo(() => {
        let res: ParticipantMetadata[] = []

        // No participants? Return nothing
        if (!participantData || participantData.length === 0) return [];

        // Order
        let ordered = participantData?.sort((a, b) => {
            if (a.label! < b.label!) return -1;
            if (a.label! > b.label!) return 1;
            return 0;
        }) ?? [];

        // Filter by instance
        const masterG = filteredMasterGroupsData?.find(x => x.name === masterGroup);
        const coordG = filteredCoordinatingGroupsData?.find(x => x.name === coordinatingGroup);
        const inst = filteredInstituteData?.find(x => x.name === institute);

        let filteredInsts: Institution[] | null = null;

        if (masterG) {
            filteredInsts = filteredInstituteData?.filter(x => x.masterGroupId === masterG.id) ?? [];
        } if (coordG) {
            filteredInsts = filteredInsts ?
                filteredInsts.filter(x => x.coordinatingGroupId === coordG.id) :
                (filteredInstituteData?.filter(x => x.coordinatingGroupId === coordG.id) ?? []);
        } if (inst) {
            filteredInsts = filteredInsts ?
                filteredInsts.filter(x => x.id === inst.id) :
                (filteredInstituteData?.filter(x => x.id === inst.id) ?? []);
        }

        if (filteredInsts !== null) {
            ordered = ordered?.filter(x => !!filteredInsts!.find(y => y.id === x.institutionId));
        }

        // If we have the patients document status
        res = ordered.map(x => {
            return {
                ...x,
                totalDocuments: participantData.find(meta => meta.id === x.id)?.totalDocuments ?? 0,
                verifiedDocuments: participantData.find(meta => meta.id === x.id)?.verifiedDocuments ?? 0
            } as ParticipantMetadata;
        });

        // Filter by users with documents
        if (!displayEmpty) {
            res = res?.filter(x => x.totalDocuments !== 0);
        }

        // Filter by verified users
        if (!displayVerified) {
            res = res?.filter(x => x.totalDocuments === 0 || ((x.verifiedDocuments ?? 0) < (x.totalDocuments ?? 0)));
        }

        return res;
    }, [
        participantData, 
        institute, 
        filteredInstituteData, 
        masterGroup, 
        filteredMasterGroupsData, 
        coordinatingGroup, 
        filteredCoordinatingGroupsData, 
        displayEmpty, 
        displayVerified
    ]);

    const ParticipantsContext = useMemo(() => {
        return {
            trialCode: (trialCode as string),
            theme,
            navigate,
            institutionMap
        }
    }, [trialCode, theme, navigate, institutionMap]);

    const loading = trialLoading || instituteDataLoading || participantLoading || trialMetaLoading;
    const validating = trialValidating || instituteDataValidating || participantValidating;

    //
    // -- Effects --
    //
    useEffect(() => {
        if (institute !== 'All' && !(filteredInstituteData?.find(x => x.name === institute)))
            setInstitute('All');
    }, [coordinatingGroup]);

    useEffect(() => {
        if (coordinatingGroup !== 'All' && !(filteredCoordinatingGroupsData?.find(x => x.name === coordinatingGroup)))
            setCoordinatingGroup('All');
    }, [masterGroup]);

    const [emailSending, setEmailSending] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState<boolean>();
    const [windowTimeout, setWindowTimeout] = useState<NodeJS.Timeout>();

    const onCallDeleteCache = useCallback(async () => {
        const request = new DeleteTrialParticipantCacheData({});

        await client.post(request);
    }, [client]);

    const onRefreshClick = useCallback(async () => {
        await onCallDeleteCache();

        await refresh();
    }, [onCallDeleteCache, refresh]);

    const handleEmailSite = (trialCoordinatorIds: number[]) => {
        const instituteCode = instituteData?.find(x => x.name === institute)?.code;
        const trialId = instituteData?.find(x => x.name == institute)?.trialId;

        if(emailSite) {
            window.clearTimeout(windowTimeout);

            setEmailSending(true);

            emailSite(trialId, instituteCode, trialCoordinatorIds)
                .then(() => {
                    setEmailSending(false);
                    setEmailSuccess(true);

                    setWindowTimeout(
                        setTimeout(() => {
                            setEmailSuccess(undefined);
                        }, 2000)
                    );
                })
                .catch(() => {
                    setEmailSending(false);
                    setEmailSuccess(false);

                    setWindowTimeout(
                        setTimeout(() => {
                            setEmailSuccess(undefined);
                        }, 2000)
                    );
                })
        }
    }

    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
        { label: "Trials", to: "/", icon: faDna },
        { label: "Participants", to: "/"+trial?.oid, icon: faUser },

        ]);
    }, [ setBreadcrumbs, trial]);
        

    return (
        <TrialInstitutionCoordinatorsContext.Provider value={trialCoordinatorsContextData}>
            <Loading name='trials' isLoading={loading} isValidating={validating} />
            <TrialCoordinatorSelectDialog 
                setDialogOpen={setCoordinatorSelectDialogOpen} 
                onAfterClose={handleEmailSite} 
                open={coordinatorSelectDialogOpen}
            />
            <Show show={!loading}>
                <>
                    <VerticalCenter sx={{ marginBottom: '15px' }} toBottom>
                        <Box>
                            <Typography color='secondary' variant='h2' whiteSpace='nowrap'>{trial?.uniqueIdentifier}</Typography>
                            <Typography color='secondary' variant='h3' whiteSpace='nowrap'>Participants</Typography>
                        </Box>
                        <VerticalCenter sx={{ width: '100%' }} display="inline" toRight gap={theme.spacing(1)}>
                            <Tooltip
                                title="Refresh Total Document Values"
                            >
                                <Box>
                                    <StyledIconButton
                                        color="primary"
                                        disabled={participantLoading || participantValidating}
                                        onClick={onRefreshClick}
                                    >
                                        {
                                            participantLoading || participantValidating ? 
                                                <CircularProgress size={18} /> :
                                                <FontAwesomeIcon icon={faArrowsRotate} size="xs"/>
                                        }
                                    </StyledIconButton>
                                </Box>
                            </Tooltip> 
                            {
                                trialVerified &&
                                <VerticalCenter sx={{
                                    backgroundColor: `${green[200]}77`,
                                    padding: '4px 8px',
                                    borderRadius: '15px'
                                }}>
                                    <FontAwesomeIcon color={green[500]} icon={faCheck} style={{ marginRight: '10px' }} />
                                    <Typography fontSize='0.8rem' fontWeight='500' color={green[500]} variant="body2">All documents verified</Typography>
                                </VerticalCenter>
                            }
                            <VerticalCenter sx={{
                                backgroundColor: `#F9CBDF`,
                                padding: '4px 8px',
                                borderRadius: '15px'
                            }}>
                                <Typography fontSize='0.8rem' fontWeight='500' color={theme.palette.secondary.main} variant="body2">{filteredParticipantData.length}</Typography>
                            </VerticalCenter>
                        </VerticalCenter>
                    </VerticalCenter>
                    <Grid 
                        container
                        spacing={2}
                    >
                        <GridSearchColumn 
                            item
                            xs={12}
                            sm={12}
                            md={4}
                            lg={4}
                            xl={4}
                        >
                            <StickySearch>
                                <List>
                                    <ListItem key={'filter-0'} sx={{ padding: '0 10px' }}>
                                        <FormControlLabel disabled={!participantData} control={<Checkbox checked={displayVerified} onClick={() => setDisplayVerified(!displayVerified)} />} sx={{ '& .MuiFormControlLabel-label': { color: theme.palette.text.primary } }} label="Display verified participants" />
                                    </ListItem>
                                    <ListItem key={'filter-1'} sx={{ padding: '0 0 0 10px' }}>
                                        <FormControlLabel disabled={!participantData} control={<Checkbox checked={displayEmpty} onClick={() => setDisplayEmpty(!displayEmpty)} />} sx={{ '& .MuiFormControlLabel-label': { color: theme.palette.text.primary } }} label="Display participants with no documents" />
                                    </ListItem>
                                    <ListItem key={'filter-3'} sx={{ padding: '15px 0 0 0' }}>
                                        <CustomSelect
                                            select
                                            label='Master Groups'
                                            fullWidth
                                            disabled={(filteredMasterGroupsData?.length ?? 0) === 0}
                                            loading={masterGroupLoading}
                                            defaultValue={'All'}
                                            value={masterGroup}
                                            onChange={(e) => setMasterGroup(e.target.value)}
                                        >
                                            <MenuItem sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }} value={'All'}>All</MenuItem>
                                            {
                                                filteredMasterGroupsData && 
                                                filteredMasterGroupsData.map((i) => (
                                                    <MenuItem key={`master-group-${i.id}`} value={i.name ?? ''} sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }}   >{i.name}</MenuItem>
                                                ))
                                            }
                                        </CustomSelect>
                                    </ListItem>
                                    <ListItem key={'filter-4'} sx={{ padding: '15px 0 0 0' }}>
                                        <CustomSelect
                                            select
                                            label='Coordinating Groups'
                                            fullWidth
                                            disabled={(filteredCoordinatingGroupsData?.length ?? 0) === 0}
                                            loading={coordinatingGroupLoading}
                                            defaultValue={'All'}
                                            value={coordinatingGroup}
                                            onChange={(e) => setCoordinatingGroup(e.target.value)}
                                        >
                                            <MenuItem sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }} value={'All'}>All</MenuItem>
                                            {
                                                filteredCoordinatingGroupsData && 
                                                filteredCoordinatingGroupsData.map((i) => (
                                                    <MenuItem 
                                                        key={`coordinating-group-${i.id}`} value={i.name ?? ''}
                                                        sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }}
                                                    >
                                                        {i.name}
                                                    </MenuItem>
                                                ))
                                            }
                                        </CustomSelect>
                                    </ListItem>
                                    <ListItem key={'filter-2'} sx={{ padding: '15px 0 0 0' }}>
                                        <CustomSelect
                                            select
                                            sx={{ 
                                                '& .MuiInputLabel-root': { color: theme.palette.text.primary } // Added theming for label color
                                            }}
                                            label='Institutions'
                                            fullWidth
                                            disabled={!trial}
                                            loading={instituteDataLoading}
                                            defaultValue={'All'}
                                            value={institute}
                                            onChange={(e) => setInstitute(e.target.value)}
                                        >
                                            <MenuItem sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }}value={'All'}>All</MenuItem>
                                            {
                                                filteredInstituteData && 
                                                filteredInstituteData.map((i) => (
                                                    <MenuItem 
                                                        key={`institution-${i.id}`} value={i.name ?? ''}
                                                        sx={{  color: theme.palette.mode === 'light' ? '#000' : '#fff' }}
                                                    >
                                                        {i.name}
                                                    </MenuItem>
                                                ))
                                            }
                                        </CustomSelect>
                                    </ListItem>
                                    <ListItem sx={{width: "100%"}}>
                                        <Center sx={{width: "100%"}}>
                                            <Button
                                                variant="contained"
                                                disabled={
                                                    emailSending ||                
                                                    !institute ||
                                                    institute == "All"
                                                }
                                                sx={{
                                                    backgroundColor: 
                                                        emailSuccess === undefined
                                                            ? theme.palette.primary.light
                                                        : emailSuccess === true
                                                            ? "#009c05"
                                                        : "#9c0000",
                                                    '&:hover': {
                                                        backgroundColor:
                                                            emailSuccess === undefined
                                                                ? darken(theme.palette.primary.light, 0.2)
                                                            : emailSuccess === true
                                                                ? darken("#009c05", 0.2)
                                                            : darken("#9c0000", 0.2)
                                                    }
                                                }}
                                                onClick={() => setCoordinatorSelectDialogOpen(true)}
                                            >
                                                {
                                                    emailSending 
                                                        ? <CircularProgress size={24.5} color="inherit" />
                                                        : "Email Site"
                                                }
                                            </Button>
                                        </Center>
                                    </ListItem>
                                </List>
                            </StickySearch>
                        </GridSearchColumn>
                        <Grid 
                            item
                            xs={12}
                            sm={12}
                            md={8}
                            lg={8}
                            xl={8}
                        >
                            <Paper>
                                <List sx={{ padding: '0' }}>
                                    <Virtuoso 
                                        useWindowScroll
                                        data={filteredParticipantData}
                                        itemContent={virtuosoItemContent}
                                        context={ParticipantsContext}
                                    />
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            </Show>
        </TrialInstitutionCoordinatorsContext.Provider>
    )
}

export default ParticipantsPage;

